import {
  Backdrop,
  Box,
  CircularProgress,
  Divider,
  Grid2,
  Menu,
  MenuItem,
  Tab,
  Tabs,
  Theme,
  useTheme,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { observer } from "mobx-react";
import { useStores } from "../../../../use-stores";
import { EmployeeRoles } from "../../../../../../models/Employees/EmployeeRoles";
import { isPodOrderType } from "../../../../../../models/Project/OrderType";
import CustomerHeading from "../../../components/projects/projectDetails/CustomerHeading";
import PodProjectOpsDisabledBanner from "../../../components/projects/PodProjectOpsDisabledBanner";
import PermissionBlockLabel from "../../../../../../views/components/common/pop-up/PermissionBlockLabel ";
import AppButton from "../../../../../../views/components/common/buttons/AppButton";
import CustomTextField from "../../../../../../views/components/common/textFields/TextField";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import CustomToggleSwitch from "../../../../../../views/components/common/switch/Switch";
import ConfirmationPopup from "../../../../../../views/components/common/pop-up/ConfirmationPopup";
import { formatDate } from "../../../../../../views/components/common/functions/formatDate";
import { useNotification } from "../../../../../../utils/NotificationsContext";
import {
  PackagingOpsStore,
  PackagingOpsInwardLineMeta,
} from "../../../../stores/ProjectManagement/PackagingOpsStore";
import PackagingOpsItemsTable from "../../../components/projects/packaging-ops/PackagingOpsItemsTable";
import PackagingOpsPacksTable from "../../../components/projects/packaging-ops/PackagingOpsPacksTable";
import CreateInventoryPopup from "./CreateInventoryPopup";
import { PackagingOpsItemRow } from "../../../../../../models/ProjectManagement/ProjectPackagingSwagpacks";
import AdjustItemPopup from "./AdjustItemPopup";
import AdjustCompletePopup from "./AdjustCompletePopup";
import AuditSkuDrawer from "./AuditSkuDrawer";
import CreatePackPopup from "./CreatePackPopup";
import EditPackPopup from "./EditPackPopup";
import EditPackItemsPopup from "./EditPackItemsPopup";
import PackItemsPopup from "./PackItemsPopup";
import InwardItemsPopup from "./InwardItemsPopup";
import EditItemMetaPopup from "./EditItemMetaPopup";
import {
  InventoryMovementRow,
  InventoryPackItem,
  InventoryPackItemInput,
  InventoryPackSummary,
  InventoryVariantUpdateRequest,
} from "../../../../../../models/inventory/InventoryAdmin";
import CreateBulkIcon from "../../../../../../assets/CreateBulk2Icon.png";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import { getModernPageColors } from "../../../../../../styles/modernListingPageSx";

type PackagingOpsTab = "items" | "packs";

const PackagingOps = () => {
  const params = useParams();
  const navigate = useNavigate();
  const theme = useTheme<Theme>();
  const colors = getModernPageColors(theme);

  const { showError, showSuccess } = useNotification();
  const { userStore, projectManagementStore, orderStore } = useStores();

  const packagingOpsStore = useMemo(() => new PackagingOpsStore(), []);

  const projectId = Number(params?.["project-id"]);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState<PackagingOpsTab>("items");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [createInventoryPopup, setCreateInventoryPopup] = useState(false);
  const [completeOrder, setCompleteOrder] = useState(false);
  const [selectedSkus, setSelectedSkus] = useState<string[]>([]);
  const [adjustItem, setAdjustItem] = useState<PackagingOpsItemRow | null>(
    null,
  );
  const [adjustCompleteItems, setAdjustCompleteItems] = useState<
    PackagingOpsItemRow[]
  >([]);
  const [inwardItems, setInwardItems] = useState<PackagingOpsItemRow[]>([]);
  const [editMetaItem, setEditMetaItem] = useState<PackagingOpsItemRow | null>(
    null,
  );
  const [createPackPopupOpen, setCreatePackPopupOpen] = useState(false);
  const [editPack, setEditPack] = useState<InventoryPackSummary | null>(null);
  const [editPackItems, setEditPackItems] =
    useState<InventoryPackSummary | null>(null);
  const [deletePack, setDeletePack] = useState<InventoryPackSummary | null>(
    null,
  );
  const [deleteSkuItem, setDeleteSkuItem] =
    useState<PackagingOpsItemRow | null>(null);
  const [viewPackItems, setViewPackItems] =
    useState<InventoryPackSummary | null>(null);
  const [viewPackItemRows, setViewPackItemRows] = useState<InventoryPackItem[]>(
    [],
  );
  const [isViewPackItemsLoading, setIsViewPackItemsLoading] = useState(false);
  const [auditItem, setAuditItem] = useState<PackagingOpsItemRow | null>(null);
  const [auditRows, setAuditRows] = useState<InventoryMovementRow[]>([]);
  const [isAuditLoading, setIsAuditLoading] = useState(false);
  const actionsAnchorRef = useRef<HTMLDivElement | null>(null);
  const [actionsMenuAnchor, setActionsMenuAnchor] =
    useState<HTMLElement | null>(null);

  const getApiErrorMessage = (error: any, fallback: string) =>
    error?.response?.data?.message ||
    error?.response?.data?.code ||
    error?.message ||
    fallback;

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm.replace(/\s+/g, " ").trim().toLowerCase());
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (!projectId || Number.isNaN(projectId)) return;

    setIsLoading(true);
    Promise.all([
      packagingOpsStore.load(projectId),
      projectManagementStore.getProjectDetails(projectId),
      orderStore.getOrderBasicDetails(projectId),
    ])
      .catch((error: any) => {
        showError(
          "Error occurred",
          getApiErrorMessage(
            error,
            "Something went wrong! Please try again later.",
          ),
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const customerId = orderStore.orderBasicDetails?.customer?.id;

  useEffect(() => {
    if (!customerId) return;
    packagingOpsStore
      .loadCustomerTags(Number(customerId))
      .catch((error: any) => {
        showError(
          "Error occurred",
          getApiErrorMessage(error, "Failed to load customer inventory tags."),
        );
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  const filteredItems = useMemo(() => {
    if (!debouncedSearch) return packagingOpsStore.itemRows;
    return packagingOpsStore.itemRows.filter((row) => {
      const haystack = [
        row.sku,
        row.clientSku,
        row.title || "",
        row.productId != null ? String(row.productId) : "",
        row.tag || "",
        row.tagId || "",
        row.boxSize || "",
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(debouncedSearch);
    });
  }, [packagingOpsStore.itemRows, debouncedSearch]);

  const filteredPacks = useMemo(() => {
    if (!debouncedSearch) return packagingOpsStore.orderPacks;
    return packagingOpsStore.orderPacks.filter((pack) => {
      const haystack = [pack.label, String(pack.packId), pack.tagId || ""]
        .join(" ")
        .toLowerCase();
      return haystack.includes(debouncedSearch);
    });
  }, [packagingOpsStore.orderPacks, debouncedSearch]);

  const selectableItemSkus = useMemo(
    () => filteredItems.map((row) => row.sku),
    [filteredItems],
  );

  useEffect(() => {
    setSelectedSkus((current) =>
      current.filter((sku) => selectableItemSkus.includes(sku)),
    );
  }, [selectableItemSkus]);

  const handleBackfillToggle = async (backfillAllowed: boolean) => {
    try {
      await projectManagementStore.updateBackfillAllowed(
        projectId,
        backfillAllowed,
      );
      setIsLoading(true);
      await packagingOpsStore.load(projectId);
    } catch (error: any) {
      showError(
        "Error",
        getApiErrorMessage(error, "Failed to update backfill allowed"),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = async () => {
    if (!projectId || Number.isNaN(projectId) || isRefreshing) return;
    setIsRefreshing(true);
    try {
      await Promise.all([
        packagingOpsStore.refreshData(projectId, Number(customerId) || null),
        projectManagementStore.getProjectDetails(projectId),
        orderStore.getOrderBasicDetails(projectId),
      ]);
    } catch (error: any) {
      showError(
        "Error occurred",
        getApiErrorMessage(error, "Failed to refresh. Please try again."),
      );
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleActionsMenuOpen = () => {
    if (!actionsAnchorRef.current) return;
    setActionsMenuAnchor(actionsAnchorRef.current);
  };

  const handleActionsMenuClose = () => {
    setActionsMenuAnchor(null);
  };

  const handleActionSelect = (actionId: string) => {
    if (actionId === "create_inventory") {
      setCreateInventoryPopup(true);
    } else if (actionId === "media_gallery") {
      if (packagingOpsStore.inventoryId) {
        navigate(
          `/projects/packaging-ops-media?image&project-id=${projectId}&inventory-id=${packagingOpsStore.inventoryId}`,
        );
      } else {
        showError("Error", "Please create or map inventory first");
      }
    } else if (actionId === "complete") {
      setCompleteOrder(true);
    } else if (actionId === "adjust_complete") {
      if (
        !userStore?.hasPermission(EmployeeRoles.UPDATE_INVENTORY_ITEMS_COUNT)
      ) {
        showError("Error", "You do not have permission to adjust inventory");
        handleActionsMenuClose();
        return;
      }
      handleOpenAdjustComplete();
    } else if (actionId === "audit_csv") {
      if (!packagingOpsStore.inventoryId) {
        showError("Error", "Please create or map inventory first");
      } else {
        navigate(
          `/inventory/count-import-logs?inventoryId=${packagingOpsStore.inventoryId}`,
        );
      }
    } else if (actionId === "mark_inwarded") {
      handleBulkInward();
    } else if (actionId === "create_pack") {
      handleOpenCreatePack();
    }
    handleActionsMenuClose();
  };

  const handleInboundRows = async (
    rows: PackagingOpsItemRow[],
    lineMeta: PackagingOpsInwardLineMeta[] = [],
  ) => {
    try {
      setIsLoading(true);
      await packagingOpsStore.inwardItems(projectId, rows, lineMeta);
      setInwardItems([]);
      setSelectedSkus([]);
    } catch (error: any) {
      showError(
        "Error occurred",
        getApiErrorMessage(
          error,
          "Failed to mark inwarded. Please try again later.",
        ),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const openInwardPopup = (rows: PackagingOpsItemRow[]) => {
    setInwardItems(rows);
  };

  const handleBulkInward = () => {
    if (selectedSkus.length === 0) {
      showError("Error", "Please select at least one SKU");
      return;
    }

    const rowsToInward = filteredItems.filter(
      (row) =>
        selectedSkus.includes(row.sku) &&
        row.remainingInward > 0 &&
        row.productId != null,
    );

    if (rowsToInward.length === 0) {
      showError(
        "Error",
        "None of the selected SKUs have pending inward quantity",
      );
      return;
    }

    openInwardPopup(rowsToInward);
  };

  const handleEditMetaSubmit = async (
    payload: InventoryVariantUpdateRequest,
  ) => {
    try {
      if (!editMetaItem) return;
      setIsLoading(true);
      await packagingOpsStore.updateVariantMeta(
        projectId,
        editMetaItem.clientSku,
        payload,
      );
      setEditMetaItem(null);
    } catch (error: any) {
      showError(
        "Error occurred",
        getApiErrorMessage(
          error,
          "Failed to update item meta. Please try again later.",
        ),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const selectedPackItems = useMemo(
    () => filteredItems.filter((row) => selectedSkus.includes(row.sku)),
    [filteredItems, selectedSkus],
  );

  const handleOpenCreatePack = () => {
    if (!packagingOpsStore.inventoryId) {
      showError("Error", "Please create or map inventory first");
      return;
    }
    if (selectedSkus.length === 0) {
      showError("Error", "Please select at least one SKU");
      return;
    }
    setCreatePackPopupOpen(true);
  };

  const handleCreatePackSubmit = async (payload: {
    label: string;
    plannedQty: number;
    items: Array<{ clientSku: string; qty: number }>;
  }) => {
    try {
      if (!packagingOpsStore.inventoryId) {
        showError("Error", "Please create or map inventory first");
        return;
      }

      setIsLoading(true);
      await packagingOpsStore.createPack(projectId, payload);
      setCreatePackPopupOpen(false);
      setSelectedSkus([]);
      setActiveTab("packs");
    } catch (error: any) {
      showError(
        "Error occurred",
        getApiErrorMessage(
          error,
          "Failed to create pack. Please try again later.",
        ),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditPackSubmit = async (payload: {
    label: string;
    plannedQty: number;
    description?: string;
  }) => {
    try {
      if (!editPack) return;

      setIsLoading(true);
      await packagingOpsStore.patchPack(projectId, editPack.packId, payload);
      setEditPack(null);
    } catch (error: any) {
      showError(
        "Error occurred",
        getApiErrorMessage(
          error,
          "Failed to update pack. Please try again later.",
        ),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeletePackConfirm = async () => {
    try {
      if (!deletePack) return;

      setIsLoading(true);
      await packagingOpsStore.deletePack(projectId, deletePack.packId);
      setDeletePack(null);
    } catch (error: any) {
      showError(
        "Error occurred",
        getApiErrorMessage(
          error,
          "Failed to delete pack. Please try again later.",
        ),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteSkuConfirm = async () => {
    try {
      if (!deleteSkuItem) return;
      if (!userStore.hasPermission(EmployeeRoles.DELETE_INVENTORY_SKU, true)) {
        showError(
          "Error",
          "You do not have permission to delete inventory SKUs",
        );
        return;
      }
      if (!packagingOpsStore.inventoryId) {
        showError("Error", "Please create or map inventory first");
        return;
      }

      setIsLoading(true);
      await packagingOpsStore.deleteVariantBySku(
        projectId,
        deleteSkuItem.clientSku,
      );
      setSelectedSkus((current) =>
        current.filter(
          (sku) => sku !== deleteSkuItem.sku && sku !== deleteSkuItem.clientSku,
        ),
      );
      setDeleteSkuItem(null);
      showSuccess("SKU deactivated successfully!");
    } catch (error: unknown) {
      showError(
        "Error occurred",
        getApiErrorMessage(
          error,
          "Failed to delete SKU. Zero out on-hand and reserved via Adjust first.",
        ),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditPackItemsSubmit = async (items: InventoryPackItemInput[]) => {
    try {
      if (!editPackItems) return;

      setIsLoading(true);
      await packagingOpsStore.replacePackItems(
        projectId,
        editPackItems.packId,
        items,
      );
      setEditPackItems(null);
    } catch (error: any) {
      showError(
        "Error occurred",
        getApiErrorMessage(
          error,
          "Failed to update pack items. Please try again later.",
        ),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewPackItems = async (pack: InventoryPackSummary) => {
    try {
      setViewPackItems(pack);
      setViewPackItemRows(pack.items || []);
      if (pack.items && pack.items.length > 0) return;

      setIsViewPackItemsLoading(true);
      const items = await packagingOpsStore.getPackDetails(pack.packId);
      setViewPackItemRows(items);
    } catch (error: any) {
      showError(
        "Error occurred",
        getApiErrorMessage(
          error,
          "Failed to load pack items. Please try again later.",
        ),
      );
      setViewPackItems(null);
      setViewPackItemRows([]);
    } finally {
      setIsViewPackItemsLoading(false);
    }
  };

  const handleToggleSelect = (clientSku: string) => {
    setSelectedSkus((current) =>
      current.includes(clientSku)
        ? current.filter((sku) => sku !== clientSku)
        : [...current, clientSku],
    );
  };

  const handleToggleSelectAll = () => {
    setSelectedSkus((current) =>
      current.length === selectableItemSkus.length ? [] : selectableItemSkus,
    );
  };

  const handleCompleteProject = () => {
    setIsLoading(true);
    packagingOpsStore
      .completeProject(projectId)
      .then((res) => {
        if (res?.success) {
          return projectManagementStore.getProjectDetails(projectId);
        }
        showError(
          "Error occurred",
          "Failed to complete project. Please try again.",
        );
      })
      .catch((error: any) =>
        showError(
          "Error occurred",
          getApiErrorMessage(
            error,
            "Something went wrong! Please try again later.",
          ),
        ),
      )
      .finally(() => setIsLoading(false));
  };

  const handleOpenAdjustComplete = () => {
    if (!userStore?.hasPermission(EmployeeRoles.UPDATE_INVENTORY_ITEMS_COUNT)) {
      showError("Error", "You do not have permission to adjust inventory");
      return;
    }
    if (!packagingOpsStore.inventoryId) {
      showError("Error", "Please create or map inventory first");
      return;
    }
    if (selectedSkus.length === 0) {
      showError("Error", "Please select at least one SKU");
      return;
    }

    const rowsToComplete = filteredItems.filter(
      (row) =>
        selectedSkus.includes(row.sku) &&
        (row.contributedUnits > 0 || row.onHand != null) &&
        !!row.clientSku?.trim(),
    );

    if (rowsToComplete.length === 0) {
      showError(
        "Error",
        "None of the selected SKUs have inventory stock to complete",
      );
      return;
    }

    const seenClientSkus = new Set<string>();
    const duplicateClientSku = rowsToComplete.find((row) => {
      const key = row.clientSku.trim().toLowerCase();
      if (seenClientSkus.has(key)) return true;
      seenClientSkus.add(key);
      return false;
    });

    if (duplicateClientSku) {
      showError(
        "Error",
        `Duplicate client SKU "${duplicateClientSku.clientSku}" in the selection`,
      );
      return;
    }

    setAdjustCompleteItems(rowsToComplete);
  };

  const handleAdjustSubmit = async (payload: {
    delta: number;
    note: string;
  }) => {
    setIsLoading(true);
    try {
      if (!packagingOpsStore.inventoryId || !adjustItem) {
        showError("Error", "Please create or map inventory first");
        return;
      }

      await packagingOpsStore.adjustItem(projectId, {
        inventoryId: packagingOpsStore.inventoryId,
        clientSku: adjustItem.clientSku,
        delta: payload.delta,
        note: payload.note,
      });
      setAdjustItem(null);
    } catch (error: any) {
      showError(
        "Error occurred",
        getApiErrorMessage(
          error,
          "Failed to adjust inventory. Please try again later.",
        ),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleAdjustCompleteSubmit = async (payload: { note: string }) => {
    setIsLoading(true);
    try {
      if (
        !userStore?.hasPermission(EmployeeRoles.UPDATE_INVENTORY_ITEMS_COUNT)
      ) {
        showError("Error", "You do not have permission to adjust inventory");
        return;
      }
      if (!packagingOpsStore.inventoryId) {
        showError("Error", "Please create or map inventory first");
        return;
      }
      if (adjustCompleteItems.length === 0) {
        showError("Error", "Please select at least one SKU");
        return;
      }

      const clientSkus = adjustCompleteItems.map((row) => row.clientSku.trim());
      const uniqueClientSkus = new Set(
        clientSkus.map((sku) => sku.toLowerCase()),
      );
      if (uniqueClientSkus.size !== clientSkus.length) {
        showError("Error", "Selected SKUs contain duplicate client SKUs");
        return;
      }

      await packagingOpsStore.adjustComplete(projectId, {
        inventoryId: packagingOpsStore.inventoryId,
        clientSkus,
        note: payload.note,
      });
      setAdjustCompleteItems([]);
      setSelectedSkus([]);
    } catch (error: any) {
      showError(
        "Error occurred",
        getApiErrorMessage(
          error,
          "Failed to complete inventory adjustment. Please try again later.",
        ),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenAudit = async (row: PackagingOpsItemRow) => {
    try {
      if (!packagingOpsStore.inventoryId) {
        showError("Error", "Please create or map inventory first");
        return;
      }

      setAuditItem(row);
      setAuditRows([]);
      setIsAuditLoading(true);
      const rows = await packagingOpsStore.getSkuMovements(row.clientSku);
      setAuditRows(rows);
    } catch (error: any) {
      showError(
        "Error occurred",
        getApiErrorMessage(
          error,
          "Failed to load audit ledger. Please try again later.",
        ),
      );
      setAuditItem(null);
    } finally {
      setIsAuditLoading(false);
    }
  };

  const projectDetails = projectManagementStore.currentProjectDetails;
  const orderBasicDetails = orderStore.orderBasicDetails;
  const isPodOrder = isPodOrderType(
    orderBasicDetails?.type_id ?? projectDetails?.order_type_id,
  );
  const canViewPackagingOps = userStore.hasPermission([
    EmployeeRoles.VIEW_ORDER,
    EmployeeRoles.VIEW_PACKAGING_OPS,
  ]);
  const canMutatePackagingOps =
    userStore.hasPermission(EmployeeRoles.CREATE_SWAGPACK) && !isPodOrder;

  return (
    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
      {isLoading &&
        inwardItems.length === 0 &&
        adjustCompleteItems.length === 0 &&
        !adjustItem && (
          <Backdrop
            sx={(theme) => ({
              color: theme.palette.customColors.white,
              zIndex: theme.zIndex.drawer + 1,
            })}
            open={isLoading}
          >
            <CircularProgress color="inherit" />
          </Backdrop>
        )}

      {canViewPackagingOps ? (
        <Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: { xs: "wrap", md: "nowrap" },
              gap: 2,
            }}
          >
            <CustomerHeading
              label={orderBasicDetails?.label || ""}
              orderId={projectId}
              moduleName="Packaging Ops"
              brandName={`${orderBasicDetails?.customer?.name || projectDetails?.customer_name || ""}`}
              inventoryId={packagingOpsStore.inventoryId}
            />

            <Box display="flex" flexDirection="column">
              <Box
                display="flex"
                flexDirection="row"
                gap={1}
                alignItems="center"
                sx={{ marginBottom: { md: "2rem" } }}
              >
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    alignItems: "center",
                    justifyContent: {
                      xs: "flex-start",
                      md: "flex-end",
                    },
                    flexWrap: "wrap",
                  }}
                >
                  {canMutatePackagingOps && (
                    <Box
                      sx={{
                        width: "180px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        border: "1px solid #86898F",
                        borderRadius: "8px",
                        height: "40px",
                        paddingLeft: "10px",
                      }}
                    >
                      <CustomTypography
                        variant="body2"
                        fontWeight={600}
                        color={theme.palette.customColors?.text}
                      >
                        Backfill Allowed
                      </CustomTypography>
                      <CustomToggleSwitch
                        checked={!!projectDetails?.backfill_allowed}
                        onChange={(e) => {
                          handleBackfillToggle(e.target.checked);
                        }}
                        activeColor={theme.palette.customColors?.purple}
                        inactiveColor={
                          theme.palette.customColors?.switchTrackGray || "#ccc"
                        }
                        disabled={
                          userStore?.hasPermission(
                            EmployeeRoles.MARK_ORDER_AS_BACKFILL_ALLOWED,
                          ) !== true
                        }
                      />
                    </Box>
                  )}
                </Box>

                {canMutatePackagingOps && (
                  <Box>
                    <Box ref={actionsAnchorRef}>
                      <AppButton
                        label="Actions"
                        onClick={handleActionsMenuOpen}
                        customStyles={{
                          height: "38px",
                          minWidth: "140px",
                        }}
                        variant="grayscale"
                      />
                    </Box>

                    <Menu
                      anchorEl={actionsMenuAnchor}
                      open={Boolean(actionsMenuAnchor)}
                      onClose={handleActionsMenuClose}
                      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                      transformOrigin={{ vertical: "top", horizontal: "left" }}
                      disableAutoFocusItem
                      slotProps={{
                        paper: {
                          sx: {
                            mt: 1,
                            borderRadius: 2,
                            border: "1px solid",
                            borderColor: "divider",
                            boxShadow:
                              "0px 20px 45px rgba(15, 23, 42, 0.15), 0px 6px 18px rgba(15, 23, 42, 0.08)",
                            minWidth: 240,
                            overflow: "hidden",
                          },
                        },
                      }}
                    >
                      <MenuItem
                        onClick={() => handleActionSelect("media_gallery")}
                        sx={{
                          fontSize: "0.9rem",
                          px: 2,
                          py: 1.25,
                          borderTop: `1px solid ${theme.palette.divider}`,
                        }}
                      >
                        <CustomTypography variant="body2" fontWeight={600}>
                          Media Gallery
                        </CustomTypography>
                      </MenuItem>
                      <MenuItem
                        onClick={() => handleActionSelect("adjust_complete")}
                        disabled={
                          !userStore?.hasPermission(
                            EmployeeRoles.UPDATE_INVENTORY_ITEMS_COUNT,
                          ) ||
                          selectedSkus.length === 0 ||
                          !packagingOpsStore.inventoryId
                        }
                        sx={{
                          fontSize: "0.9rem",
                          px: 2,
                          py: 1.25,
                          borderTop: `1px solid ${theme.palette.divider}`,
                          "&.Mui-disabled": { opacity: 0.4 },
                        }}
                      >
                        <CustomTypography variant="body2" fontWeight={600}>
                          Adjust Complete
                        </CustomTypography>
                      </MenuItem>
                      <MenuItem
                        onClick={() => handleActionSelect("audit_csv")}
                        disabled={!packagingOpsStore.inventoryId}
                        sx={{
                          fontSize: "0.9rem",
                          px: 2,
                          py: 1.25,
                          borderTop: `1px solid ${theme.palette.divider}`,
                          "&.Mui-disabled": { opacity: 0.4 },
                        }}
                      >
                        <CustomTypography variant="body2" fontWeight={600}>
                          Audit CSV Updates
                        </CustomTypography>
                      </MenuItem>
                      <MenuItem
                        onClick={() => handleActionSelect("complete")}
                        disabled={!!projectDetails?.archived}
                        sx={{
                          fontSize: "0.9rem",
                          px: 2,
                          py: 1.25,
                          borderTop: `1px solid ${theme.palette.divider}`,
                          "&.Mui-disabled": { opacity: 0.4 },
                        }}
                      >
                        <CustomTypography variant="body2" fontWeight={600}>
                          Complete
                        </CustomTypography>
                      </MenuItem>
                    </Menu>
                  </Box>
                )}
              </Box>
              <CustomTypography
                variant="body2"
                color={theme.palette.customColors?.text}
                fontWeight={600}
                sx={{ textAlign: { xs: "left", md: "right" } }}
              >
                Backfill status updated by{" "}
                {projectDetails?.backfill_allowed_by_name || "—"} on{" "}
                {formatDate(projectDetails?.backfill_allowed_date || "")}
              </CustomTypography>
            </Box>
          </Box>

          <Divider sx={{ mt: 2, mb: 3 }} />

          <Grid2
            container
            spacing={2}
            alignItems="center"
            sx={{ mb: 2 }}
            display="flex"
            justifyContent="space-between"
          >
            <Grid2 size={{ xs: 12, md: 7 }}>
              <CustomTextField
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value || "")}
                width="100%"
                placeholder={
                  activeTab === "items"
                    ? "Search by SKU, title, or product ID"
                    : "Search by pack name or ID"
                }
                customStyles={{
                  backgroundColor:
                    theme?.palette?.customColors?.grayBackGroundColor,
                }}
              />
            </Grid2>
            <Grid2 size={{ xs: 12, md: 5 }}>
              <Box
                display="flex"
                flexDirection="row"
                flexWrap="wrap"
                gap={2}
                justifyContent={{ xs: "flex-start", md: "flex-end" }}
                alignItems="center"
              >
                <AppButton
                  label={isRefreshing ? "Refreshing..." : "Refresh"}
                  startIcon={<RefreshIcon />}
                  onClick={() => {
                    void handleRefresh();
                  }}
                  disabled={isRefreshing || isLoading}
                  customStyles={{
                    minWidth: "9.438rem",
                    height: 34,
                    px: 1.5,
                    borderRadius: "999px",
                    color: colors.title,
                    backgroundColor: colors.surface,
                    border: `1px solid ${colors.border}`,
                    "&:hover": {
                      backgroundColor: colors.surfaceMuted,
                      color: colors.title,
                    },
                  }}
                />
                {canMutatePackagingOps &&
                  userStore?.hasPermission(EmployeeRoles.CREATE_SWAGPACK) && (
                    <AppButton
                      label="Create Pack"
                      customStyles={{
                        height: "38px",
                        minWidth: "9.438rem",
                        color: theme?.palette?.customColors?.blackBorder,
                      }}
                      startIcon={<AddIcon />}
                      variant="grayscale"
                      onClick={() => handleActionSelect("create_pack")}
                      disabled={selectedSkus.length === 0}
                    />
                  )}
                {canMutatePackagingOps &&
                  userStore?.hasPermission(EmployeeRoles.CREATE_SWAGPACK) && (
                    <AppButton
                      label="Mark Inward"
                      customStyles={{
                        height: "38px",
                        minWidth: "9.438rem",
                        backgroundColor: theme?.palette?.customColors?.purple,
                        color: theme?.palette?.customColors?.white,
                      }}
                      variant="primary"
                      startIcon={CreateBulkIcon}
                      onClick={() => handleActionSelect("mark_inwarded")}
                      disabled={selectedSkus.length === 0}
                    />
                  )}
              </Box>
            </Grid2>
            {/* <Grid2 size={{ xs: 12, md: 6 }}>
              {activeTab === "items" && (
                <Box
                  display="flex"
                  justifyContent={{ xs: "flex-start", md: "flex-end" }}
                  gap={2}
                >
                  <CustomTypography variant="body2" fontWeight={600}>
                    Remaining inward (total):{" "}
                    {packagingOpsStore.totalRemainingInward}
                  </CustomTypography>
                </Box>
              )}
            </Grid2> */}
          </Grid2>

          {isPodOrder ? <PodProjectOpsDisabledBanner /> : null}

          <Tabs
            value={activeTab}
            onChange={(_, value: PackagingOpsTab) => setActiveTab(value)}
            sx={{
              mb: 0,
              ".MuiTabs-indicator": {
                backgroundColor: theme.palette.customColors?.purple,
              },
            }}
          >
            <Tab
              value="items"
              label={`ITEMS (${filteredItems.length})`}
              sx={{ textTransform: "none", fontWeight: 600 }}
            />
            <Tab
              value="packs"
              label={`PACKS (${filteredPacks.length})`}
              sx={{ textTransform: "none", fontWeight: 600 }}
            />
          </Tabs>

          {activeTab === "items" ? (
            <PackagingOpsItemsTable
              rows={filteredItems}
              isLoading={isLoading}
              selectedSkus={selectedSkus}
              showSelection={canMutatePackagingOps}
              showActions={canMutatePackagingOps}
              onToggleSelect={handleToggleSelect}
              onToggleSelectAll={handleToggleSelectAll}
              onInboundRow={(row) => {
                openInwardPopup([row]);
              }}
              onAdjustRow={(row) => {
                setAdjustItem(row);
              }}
              onEditMetaRow={(row) => {
                setEditMetaItem(row);
              }}
              onAuditRow={(row) => {
                void handleOpenAudit(row);
              }}
              canDeleteSku={userStore.hasPermission(
                EmployeeRoles.DELETE_INVENTORY_SKU,
                true,
              )}
              onDeleteRow={(row) => setDeleteSkuItem(row)}
              onProductClick={(productId) => {
                navigate(`/products/${productId}`);
              }}
            />
          ) : (
            <PackagingOpsPacksTable
              packs={filteredPacks}
              isLoading={isLoading}
              showActions={canMutatePackagingOps}
              onEditPack={(pack) => setEditPack(pack)}
              onEditPackItems={(pack) => setEditPackItems(pack)}
              onDeletePack={(pack) => setDeletePack(pack)}
              onViewItems={(pack) => {
                void handleViewPackItems(pack);
              }}
            />
          )}

          {completeOrder && (
            <ConfirmationPopup
              open={completeOrder}
              id={projectId}
              title="Confirm Completion"
              content="Are you sure you want to complete this order ?"
              onConfirm={() => {
                handleCompleteProject();
                setCompleteOrder(false);
              }}
              onCancel={() => setCompleteOrder(false)}
            />
          )}

          {deletePack && (
            <ConfirmationPopup
              open={!!deletePack}
              id={deletePack.packId}
              title="Delete Pack"
              content={`Are you sure you want to delete pack "${deletePack.label}"? This will mark the pack as inactive.`}
              onConfirm={() => {
                void handleDeletePackConfirm();
              }}
              onCancel={() => setDeletePack(null)}
            />
          )}

          {deleteSkuItem && (
            <ConfirmationPopup
              open={!!deleteSkuItem}
              id={0}
              title="Delete SKU"
              content={`Are you sure you want to deactivate SKU "${deleteSkuItem.clientSku}"? This frees it for re-registration. History is retained. On-hand and reserved must already be zero.`}
              zIndex={2100}
              onConfirm={() => {
                void handleDeleteSkuConfirm();
              }}
              onCancel={() => setDeleteSkuItem(null)}
            />
          )}

          <CreateInventoryPopup
            open={createInventoryPopup}
            onClose={() => setCreateInventoryPopup(false)}
            orderId={projectId}
            customerId={
              orderBasicDetails?.customer?.id ??
              orderBasicDetails?.customer_id ??
              0
            }
            labelInputText="Enter Inventory Label"
            onCreate={({ customerId, label, orderId, type }) => {
              setIsLoading(true);
              setCreateInventoryPopup(false);
              packagingOpsStore
                .createInventory(customerId, label, orderId, type)
                .then(async () => {
                  try {
                    await orderStore.getOrderBasicDetails(projectId);
                  } catch (refreshError) {
                    console.error(
                      "Failed to refresh order details after inventory create:",
                      refreshError,
                    );
                  }
                })
                .catch((error: any) => {
                  showError(
                    "Error occurred",
                    getApiErrorMessage(
                      error,
                      "Something went wrong! Please try again later.",
                    ),
                  );
                })
                .finally(() => {
                  setIsLoading(false);
                });
            }}
          />
          <AdjustItemPopup
            open={!!adjustItem}
            item={adjustItem}
            isSubmitting={isLoading && !!adjustItem}
            onClose={() => setAdjustItem(null)}
            onSubmit={handleAdjustSubmit}
          />
          <AdjustCompletePopup
            open={adjustCompleteItems.length > 0}
            items={adjustCompleteItems}
            isSubmitting={isLoading && adjustCompleteItems.length > 0}
            onClose={() => {
              if (isLoading) return;
              setAdjustCompleteItems([]);
            }}
            onSubmit={async (payload) => {
              await handleAdjustCompleteSubmit(payload);
            }}
          />
          <InwardItemsPopup
            open={inwardItems.length > 0}
            items={inwardItems}
            tags={packagingOpsStore.customerTags}
            isSubmitting={isLoading && inwardItems.length > 0}
            onClose={() => {
              if (isLoading) return;
              setInwardItems([]);
            }}
            onSubmit={(lineMeta) => {
              void handleInboundRows(inwardItems, lineMeta);
            }}
          />
          <EditItemMetaPopup
            open={!!editMetaItem}
            item={editMetaItem}
            tags={packagingOpsStore.customerTags}
            onCreateTag={
              customerId
                ? (tagId, tag) =>
                    packagingOpsStore.createCustomerTag(
                      Number(customerId),
                      tagId,
                      tag,
                    )
                : undefined
            }
            onClose={() => setEditMetaItem(null)}
            onSubmit={(payload) => {
              void handleEditMetaSubmit(payload);
            }}
          />
          <CreatePackPopup
            open={createPackPopupOpen}
            items={selectedPackItems}
            onClose={() => setCreatePackPopupOpen(false)}
            onSubmit={(payload) => {
              void handleCreatePackSubmit(payload);
            }}
          />
          <EditPackPopup
            open={!!editPack}
            pack={editPack}
            onClose={() => setEditPack(null)}
            onSubmit={(payload) => {
              void handleEditPackSubmit(payload);
            }}
          />
          <EditPackItemsPopup
            open={!!editPackItems}
            pack={editPackItems}
            isSubmitting={isLoading && !!editPackItems}
            onClose={() => {
              if (isLoading) return;
              setEditPackItems(null);
            }}
            onSearchVariants={(search) =>
              packagingOpsStore.searchVariants(search)
            }
            onLoadPackItems={(packId) =>
              packagingOpsStore.getPackDetails(packId)
            }
            onSubmit={async (items) => {
              await handleEditPackItemsSubmit(items);
            }}
          />
          <PackItemsPopup
            open={!!viewPackItems}
            pack={viewPackItems}
            items={viewPackItemRows}
            isLoading={isViewPackItemsLoading}
            onClose={() => {
              setViewPackItems(null);
              setViewPackItemRows([]);
            }}
            onProductClick={(productId) => {
              navigate(`/products/${productId}`);
            }}
          />
          <AuditSkuDrawer
            open={!!auditItem}
            clientSku={auditItem?.clientSku || null}
            productName={auditItem?.productName || null}
            rows={auditRows}
            isLoading={isAuditLoading}
            onClose={() => {
              setAuditItem(null);
              setAuditRows([]);
            }}
          />
        </Box>
      ) : (
        <PermissionBlockLabel />
      )}
    </Grid2>
  );
};

export default observer(PackagingOps);
